// The queue's column arrangement, as an ordered checklist.
//
// WHAT THIS EDITS is what every agent who opens this view sees on /tickets: which
// columns appear and in what order. `columns_json` has been on `saved_views` since
// migration 012 and the queue screen has always read it (pages/tickets/List.jsx, through
// `visibleColumns`), which silently drops any key it does not know. What was missing was
// somewhere to choose it.
//
// ORDER IS THE POINT, so this is not a multi-select. A checklist with no order would
// let an admin pick columns and not decide which comes first, and the queue would fall
// back to the catalogue's order — which is the arrangement they were trying to change.
// Selected columns sit at the top in their chosen order with arrows; the rest sit
// below, unchecked, in the catalogue's order.
//
// LABELS COME FROM THE BROWSER CATALOGUE and the KEYS from the server's vocabulary,
// which is the same split `sort` uses. The server will not accept a key it did not
// serve, so a picker built from `field.vocabulary.keys` cannot offer an arrangement the
// write path refuses. Anything the server offers that this build has no label for is
// still listed, under its key — dropping it silently would hide a column the view can
// legitimately have.
//
// EVERY SENTENCE COMES FROM ../../components/tickets/ticket-columns.js and is pure and
// tested. Nothing here decides what an arrangement means.

import { TICKET_COLUMNS, selectionProblem } from '../tickets/ticket-columns.js';

const TICKET_LABELS = new Map(TICKET_COLUMNS.map((c) => [c.key, c.label]));

/**
 * How to name a key.
 *
 * The ticket catalogue by default, because that is what this control edits on a saved
 * view. The Settings screen reuses the same control for the CUSTOMER arrangement, whose
 * keys the ticket catalogue has never heard of — passing a labeller beats a second copy
 * of this component that differs only in a Map.
 *
 * Falls through to the raw key rather than rendering blank: a key this build has no
 * label for is still a column the server offered, and an unlabelled checkbox is worse
 * than an ugly one.
 */
const defaultLabelFor = (key) => TICKET_LABELS.get(key) ?? key;

export default function ColumnsField({
  id, field, fieldKey, value, setValue,
  labelFor = defaultLabelFor,
  // Which arrangements this list will honour. The ticket rule by default; the
  // Settings screen passes the customers one, whose openable column is different.
  problemFor = selectionProblem,
  scope = 'everyone who opens this view',
}) {
  // The server's list, not the browser's: it is the authority on what may be saved.
  const offered = field?.vocabulary?.keys ?? TICKET_COLUMNS.map((c) => c.key);
  const chosen = Array.isArray(value) ? value.filter((k) => offered.includes(k)) : [];
  const rest = offered.filter((k) => !chosen.includes(k));

  const set = (next) => setValue(fieldKey, next);
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= chosen.length) return;
    const next = [...chosen];
    [next[index], next[target]] = [next[target], next[index]];
    set(next);
  };

  const problem = problemFor(chosen);

  return (
    <div id={id} className="mt-1 rounded-lg border border-line">
      {chosen.length > 0 && (
        <ul className="divide-y divide-line">
          {chosen.map((key, index) => (
            <li key={key} className="flex items-center gap-2 px-3 py-1.5">
              <div className="flex shrink-0 flex-col">
                <button
                  type="button"
                  className="px-1 text-xs text-ink-muted hover:text-ink disabled:opacity-30"
                  disabled={index === 0}
                  aria-label={`Move ${labelFor(key)} left`}
                  onClick={() => move(index, -1)}
                >
                  ▲
                </button>
                <button
                  type="button"
                  className="px-1 text-xs text-ink-muted hover:text-ink disabled:opacity-30"
                  disabled={index === chosen.length - 1}
                  aria-label={`Move ${labelFor(key)} right`}
                  onClick={() => move(index, 1)}
                >
                  ▼
                </button>
              </div>
              <span className="min-w-0 flex-1 truncate text-sm text-ink">
                {labelFor(key)}
                <span className="ml-2 text-xs text-ink-muted">{index + 1}</span>
              </span>
              <button
                type="button"
                className="shrink-0 text-xs text-ink-muted hover:text-red-700 dark:hover:text-red-300"
                onClick={() => set(chosen.filter((k) => k !== key))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {rest.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-line px-3 py-2">
          {rest.map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm text-ink-secondary">
              <input
                type="checkbox"
                className="size-4 rounded accent-brand-600"
                checked={false}
                onChange={() => set([...chosen, key])}
              />
              {labelFor(key)}
            </label>
          ))}
        </div>
      )}

      {/* Said BEFORE saving, not after. A selection the queue will not honour is the
          worst outcome here: the admin arranges it, saves successfully, and every agent
          keeps seeing the old arrangement with nothing to explain why. */}
      <p className={`border-t border-line px-3 py-2 text-xs ${
        problem.ok ? 'text-ink-muted' : 'text-amber-700 dark:text-amber-400'}`}
      >
        {problem.ok
          ? `${chosen.length} column${chosen.length === 1 ? '' : 's'}, left to right, for ${scope}.`
          : problem.reason}
      </p>
    </div>
  );
}
